import { Box, Card, CardContent, Checkbox, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { FileIcon, defaultStyles } from 'react-file-icon';
import imagesAPI from '../components/images';
import { ContextStore } from '../store/contextStore';

const useStyles = makeStyles((theme) => ({
    card: {
        cursor: 'pointer',
        borderRadius: '12px',
        border: '1px solid #D8D8D8',
        backgroundColor: '#F9FAFB',
        transition: 'all 0.2s ease',
        width: '203px',
        boxShadow: 'none !important',
        '&:hover': {
            borderColor: '#007AFF',
        },
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    cardSelected: {
        backgroundColor: '#EAF4FF',
        borderColor: '#007AFF',
    },
    previewWrapper: {
        padding: '12px',
        position: 'relative',
    },
    previewGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '4px',
        width: '179px',
        height: '179px',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        [theme.breakpoints.down('md')]: {
            width: '100%',
            height: 'auto',
            aspectRatio: '1',
        },
    },
    previewImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    fileIconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: '#F5F5F5',
    },
    fileIconWrapper: {
        width: '50px',
        height: '63px',
    },
    overlay: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        background: 'linear-gradient(185deg, rgba(0, 0, 0, 0) 11%, rgba(0, 0, 0, 0.3) 91%)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px 0 0 0',
        fontSize: '14px',
        fontWeight: 500,
    },
    checkboxWrapper: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 1,
    },
    content: {
        padding: '12px !important',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#000000',
        lineHeight: '1.4em',
        minHeight: '2.8em',
    },
    metadata: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#667085',
    },
    divider: {
        width: '0',
        height: '10px',
        borderLeft: '1px solid #667085',
    },
}));

const TourMediaCard = React.memo(({ tour, index, selected, onSelect, onClick }) => {
    const classes = useStyles();
    const { settings } = useContext(ContextStore);
    const [imageErrors, setImageErrors] = useState({});

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(tour.id, index, e);
    };

    const handleCardClick = (e) => {
        if (!e.defaultPrevented) {
            onClick(tour.id);
        }
    };

    const handleImageError = (index) => {
        setImageErrors(prev => ({ ...prev, [index]: true }));
    };

    const formatDate = (dateStr) => {
        return moment(dateStr).format('MMM DD, YYYY');
    };

    const formatTime = (dateStr) => {
        return moment(dateStr).format('hh:mm A');
    };

    // Prepare preview: for image we construct URL through the image service, for video - null (show icon)
    const previewItems = (tour.preview_images || []).map((img) => {
        if (!img || !img.p || !img.s) return null;
        return {
            url: img.t === 'image' ? imagesAPI.url(settings.images.base, img, 'cover_88x88') : null,
            type: img.t || 'image',
            extension: img.s.split('.').pop()?.toLowerCase() || 'file',
        };
    }).filter(Boolean);

    const extraCount = Math.max(0, (tour.media_count || 0) - 4);

    return (
        <Card
            className={`${classes.card} ${selected ? classes.cardSelected : ''}`}
            onClick={handleCardClick}
        >
            <Box className={classes.previewWrapper}>
                <Box className={classes.checkboxWrapper}>
                    <Checkbox
                        checked={selected}
                        onClick={handleCheckboxClick}
                        sx={{
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            '&.Mui-checked': {
                                color: '#007AFF',
                                backgroundColor: 'white',
                            },
                        }}
                    />
                </Box>
                <Box className={classes.previewGrid}>
                    {previewItems.slice(0, 4).map((item, index) => {
                        const hasError = imageErrors[index];

                        // For video or on error - show file icon
                        if (item.type === 'video' || hasError || !item.url) {
                            return (
                                <Box
                                    key={`file-${tour.id}-${index}`}
                                    className={classes.fileIconContainer}
                                >
                                    <Box className={classes.fileIconWrapper}>
                                        <FileIcon
                                            extension={item.extension}
                                            {...defaultStyles[item.extension]}
                                        />
                                    </Box>
                                </Box>
                            );
                        }

                        return (
                            <img
                                key={`img-${tour.id}-${index}`}
                                src={item.url}
                                alt=""
                                className={classes.previewImage}
                                loading="lazy"
                                decoding="async"
                                onError={() => handleImageError(index)}
                            />
                        );
                    })}
                    {extraCount > 0 && (
                        <Box className={classes.overlay}>+{extraCount}</Box>
                    )}
                </Box>
            </Box>
            <CardContent className={classes.content}>
                <Tooltip title={tour.name} placement="top">
                    <Box
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#000000',
                            lineHeight: '1.4em',
                            minHeight: '2.8em',
                        }}
                    >
                        {tour.name} ({tour.media_count} files)
                    </Box>
                </Tooltip>
                <Box className={classes.metadata}>
                    <span>{formatDate(tour.first_date)}</span>
                    <Box className={classes.divider} />
                    <span>{formatTime(tour.first_date)}</span>
                </Box>
            </CardContent>
        </Card>
    );
});

TourMediaCard.displayName = 'TourMediaCard';

TourMediaCard.propTypes = {
    tour: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        media_count: PropTypes.number,
        first_date: PropTypes.string,
        preview_images: PropTypes.arrayOf(PropTypes.shape({
            p: PropTypes.string,
            s: PropTypes.string,
            t: PropTypes.string,
        })),
    }).isRequired,
    index: PropTypes.number.isRequired,
    selected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
};

export default TourMediaCard;

